import { ref, watch } from "vue";
import { useFeatureFlagStatus } from "@/services/feature_flags";
import {
  DEFAULT_REASONING_EFFORT,
  REASONING_EFFORT_OPTIONS,
  type ReasoningEffort,
} from "@/services/ai_agent";

const STORAGE_KEY = "aiChatSession";
const EFFORT_STORAGE_KEY = "aiChatEffort";

function restoreEffort(): ReasoningEffort {
  try {
    const stored = sessionStorage.getItem(EFFORT_STORAGE_KEY);
    if (
      stored &&
      REASONING_EFFORT_OPTIONS.some((opt) => opt.value === stored)
    ) {
      return stored as ReasoningEffort;
    }
  } catch {
    // Private mode / blocked storage: fall through to the default.
  }
  return DEFAULT_REASONING_EFFORT;
}

function persistEffort(value: ReasoningEffort): void {
  try {
    sessionStorage.setItem(EFFORT_STORAGE_KEY, value);
  } catch {
    // Nothing to do — the in-memory ref is still the source of truth.
  }
}

function loadPersistedSessionId(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.sessionId === "number") {
      return parsed.sessionId;
    }
  } catch {
    // Corrupted data — ignore
  }
  return null;
}

function persistSession(sessionId: number | null): void {
  if (sessionId) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId }));
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

// Restore session ID from sessionStorage (survives full page reloads).
// Phase stays "prompt" until the session is validated by the chat panel.
const persistedSessionId = loadPersistedSessionId();

// Module-level shared state (singleton across all consumers)
const isOpen = ref(false);
const phase = ref<"prompt" | "chat">("prompt");
const sessionId = ref<number | null>(persistedSessionId);
const hasLocalButton = ref(false);
const pendingPrompt = ref<string | null>(null);
const pendingInitialMessage = ref<string | null>(null);
const panelWidth = ref(448);
const isResizing = ref(false);
// Shared reasoning-effort selection: both AIPromptPanel and AIChatPanel read
// and write this, so the value picked before the first message carries into
// the running chat and vice versa. Persisted alongside the session id for the
// same reason that is: a reload that restores the conversation but resets the
// control to Low shows a setting the next message would not have used.
const chatEffort = ref<ReasoningEffort>(restoreEffort());

// Keep sessionStorage in sync
watch(sessionId, (val) => persistSession(val));
watch(chatEffort, (val) => persistEffort(val));

/** Reset all state. Call on logout and organization switch. */
export function resetAIChatState(): void {
  isOpen.value = false;
  phase.value = "prompt";
  sessionId.value = null;
  hasLocalButton.value = false;
  pendingPrompt.value = null;
  pendingInitialMessage.value = null;
  panelWidth.value = 448;
  isResizing.value = false;
  chatEffort.value = DEFAULT_REASONING_EFFORT;
  persistEffort(DEFAULT_REASONING_EFFORT);
}

export function useAIChatSlideover() {
  const {
    isVisible: aiChatVisible,
    isUsable: allowAiChat,
    consentPending: aiChatConsentPending,
  } = useFeatureFlagStatus("aiChat");

  function toggle(): void {
    isOpen.value = !isOpen.value;
  }

  function open(): void {
    phase.value = sessionId.value ? "chat" : "prompt";
    isOpen.value = true;
  }

  function close(): void {
    isOpen.value = false;
  }

  function handleSessionCreated(id: number): void {
    sessionId.value = id;
    phase.value = "chat";
  }

  function restart(): void {
    sessionId.value = null;
    phase.value = "prompt";
  }

  function openWithPrompt(prompt: string): void {
    pendingPrompt.value = prompt;
    restart();
    isOpen.value = true;
  }

  return {
    isOpen,
    phase,
    sessionId,
    aiChatVisible,
    allowAiChat,
    aiChatConsentPending,
    hasLocalButton,
    panelWidth,
    isResizing,
    pendingPrompt,
    pendingInitialMessage,
    chatEffort,
    toggle,
    open,
    close,
    handleSessionCreated,
    restart,
    openWithPrompt,
  };
}
